import React from "react";
import { SplitButton } from "@design-system/Button/SplitButton";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { Menu } from "@design-system/Menu/Menu.lib";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { NATIVE_INVOICE_NEGATIVE_TOTAL_TOOLTIP } from "@components/invoices/invoiceLockedFieldTooltip";
import type { SaveStatus } from "./useNativeInvoiceSave";
import styles from "./NativeInvoiceDrawer.module.scss";

interface Props {
    open: boolean;
    saveStatus: SaveStatus;
    isDisabled: boolean;
    // The backend rejects a negative invoice total on create/update; when this
    // is the reason isDisabled is true, surface an explanatory tooltip rather
    // than a silently-disabled button (isDisabled also covers the invalid
    // template-name case, which has its own inline field error instead).
    hasNegativeTotal?: boolean;
    onCreate: () => void;
    // Creates the template and immediately starts a create-invoice session
    // with it applied, instead of ending on the templates list.
    onCreateAndInvoice: () => void;
}

export const TemplateCreationCommandBar: React.FC<Props> = ({
    open,
    saveStatus,
    isDisabled,
    hasNegativeTotal = false,
    onCreate,
    onCreateAndInvoice,
}) => (
    <CommandIsland className={styles.commandBar}>
        <div className={styles.commandBarGroup} />
        <div className={styles.commandBarGroup}>
            {/* mod+enter follows the default (Create template). */}
            <WithHotkey
                hotKey="mod+enter"
                hotKeyDisplayPosition="inline"
                hotKeyOptions={{
                    description: "Create template",
                    enabled: open && !isDisabled,
                    // The panel is a form: without this the shortcut dies
                    // while the caret sits in any field, which is where it
                    // is used from.
                    enableOnFormTags: true,
                    preventDefault: true,
                }}
            >
                {(hotkeyHint) => (
                    <Tooltip
                        content={
                            hasNegativeTotal
                                ? NATIVE_INVOICE_NEGATIVE_TOTAL_TOOLTIP
                                : undefined
                        }
                    >
                        <SplitButton
                            variant="primary"
                            size="md"
                            onPrimaryClick={onCreate}
                            loading={saveStatus === "saving"}
                            disabled={isDisabled}
                            menuAriaLabel="More create options"
                            menu={
                                <Menu.Item
                                    disabled={isDisabled}
                                    disabledReason={
                                        hasNegativeTotal
                                            ? NATIVE_INVOICE_NEGATIVE_TOTAL_TOOLTIP
                                            : undefined
                                    }
                                    onClick={onCreateAndInvoice}
                                >
                                    Create template and apply
                                </Menu.Item>
                            }
                        >
                            Create template
                            {hotkeyHint}
                        </SplitButton>
                    </Tooltip>
                )}
            </WithHotkey>
        </div>
    </CommandIsland>
);
